import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Siren } from "lucide-react";
import { MISSING_FIELDS_EVENT } from "@/lib/uiSounds";

const AUTO_CLOSE_MS = 4000;

// Avviso al centro dello schermo quando il cliente prova ad andare avanti o salvare
// senza aver compilato i campi obbligatori (accompagna la sirena). Non blocca nulla:
// si chiude da solo, con un tocco ovunque o con Esc.
export default function MissingFieldsAlert() {
  const [alert, setAlert] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    const show = (event) => {
      setAlert({ message: event.detail?.message || "", key: Date.now() });
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setAlert(null), AUTO_CLOSE_MS);
    };
    window.addEventListener(MISSING_FIELDS_EVENT, show);
    return () => { window.removeEventListener(MISSING_FIELDS_EVENT, show); clearTimeout(timer.current); };
  }, []);

  useEffect(() => {
    if (!alert) return undefined;
    const close = (event) => { if (event.key === "Escape") setAlert(null); };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [alert]);

  if (!alert) return null;
  const close = () => { clearTimeout(timer.current); setAlert(null); };
  return createPortal(
    <div key={alert.key} className="fixed inset-0 z-[120] flex items-center justify-center bg-navy/40 p-4 backdrop-blur-[2px] animate-in fade-in-0 duration-150 motion-reduce:animate-none"
      role="presentation" onClick={close} data-testid="missing-fields-alert">
      <div role="alertdialog" aria-modal="true" aria-labelledby="missing-fields-title" aria-describedby="missing-fields-description"
        className="w-full max-w-md rounded-2xl border-2 border-red-200 bg-white p-6 text-center shadow-2xl animate-in zoom-in-95 duration-200 motion-reduce:animate-none sm:p-8">
        <div className="relative mx-auto mb-5 grid h-20 w-20 place-items-center">
          <span className="absolute inset-0 rounded-full bg-red-400/40 animate-ping motion-reduce:hidden" aria-hidden="true" />
          <span className="relative grid h-20 w-20 place-items-center rounded-full bg-red-50 text-red-600 ring-4 ring-red-100">
            <Siren className="h-10 w-10" strokeWidth={1.8} />
          </span>
        </div>
        <h2 id="missing-fields-title" className="font-heading text-2xl font-bold text-navy">Compila i campi evidenziati in rosso</h2>
        <p id="missing-fields-description" className="mt-2 leading-relaxed text-slate-600">per poter andare avanti e salvare.</p>
        {alert.message && <p className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{alert.message}</p>}
        <button type="button" onClick={close} autoFocus
          className="mt-6 inline-flex min-h-[48px] w-full items-center justify-center rounded-lg bg-navy px-5 text-sm font-medium text-white hover:bg-navy-600"
          data-testid="missing-fields-ok">Ho capito</button>
      </div>
    </div>,
    document.body,
  );
}
